import React from 'react';
import { Row, Col, Typography, Avatar, Divider, Card } from 'antd';
import styled from 'styled-components';
import { 
  EditOutlined, 
  EnvironmentOutlined, 
  MailOutlined, 
  LinkedinOutlined,
  InstagramOutlined 
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

// Mock data for demo
const mockProfileData = {
  firstName: 'Alex',
  lastName: 'Rivera',
  title: 'Digital Content Creator',
  school: 'UCLA - University of California, Los Angeles',
  location: 'Los Angeles, CA',
  email: 'alex.rivera@ucla.edu',
  bio: 'Passionate digital content creator specializing in social media marketing, photography, and video production. I love creating engaging content that tells compelling stories and connects brands with their audiences.',
  skills: ['Video Editing', 'Photography', 'Social Media Marketing', 'Adobe Creative Suite', 'Content Strategy'],
  interests: ['Sustainability', 'Fashion', 'Technology', 'Travel', 'Music'],
  experience: [
    {
      title: 'Social Media Intern',
      company: 'Creative Agency LA',
      duration: 'Jun 2023 - Present',
      description: 'Created engaging social media content for 5+ brand clients, increasing engagement by 40%'
    },
    {
      title: 'Freelance Photographer',
      company: 'Self-Employed',
      duration: 'Jan 2022 - Present',
      description: 'Portrait and event photography for local businesses and personal clients'
    }
  ],
  education: {
    school: 'UCLA',
    degree: 'Bachelor of Arts in Communications',
    duration: '2021 - 2025'
  }
};

// Styled Components
const ProfileContainer = styled.div`
  background: ${props => props.theme.colors?.neutral?.[950] || '#0a0a0a'};
  min-height: 600px;
  position: relative;
`;

const ProfileHeader = styled.div`
  height: 200px;
  background: linear-gradient(135deg, #e22784 0%, #fc673d 100%);
  position: relative;
  display: flex;
  align-items: flex-end;
  padding: ${props => props.theme.spacing?.[6] || '1.5rem'};
`;

const ProfileContent = styled.div`
  padding: ${props => props.theme.spacing?.[6] || '1.5rem'};
  margin-top: -80px;
  position: relative;
  z-index: 2;
`;

const ProfileAvatar = styled(Avatar)`
  && {
    width: 120px;
    height: 120px;
    border: 4px solid white;
    box-shadow: ${props => props.theme.boxShadow?.lg || '0 10px 15px -3px rgba(0, 0, 0, 0.1)'};
    margin-bottom: ${props => props.theme.spacing?.[4] || '1rem'};
  }
`;

const ProfileInfo = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-radius: ${props => props.theme.borderRadius?.lg || '0.5rem'};
  padding: ${props => props.theme.spacing?.[6] || '1.5rem'};
  margin-bottom: ${props => props.theme.spacing?.[6] || '1.5rem'};
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const ProfileName = styled(Title)`
  && {
    color: ${props => props.theme.colors?.neutral?.[50] || '#fafafa'};
    margin-bottom: ${props => props.theme.spacing?.[1] || '0.25rem'};
    font-family: ${props => props.theme.typography?.fontFamily?.heading || "'Hanson Bold', 'Inter', system-ui, sans-serif"};
  }
`;

const ProfileTitle = styled(Text)`
  && {
    color: ${props => props.theme.colors?.secondary?.[400] || '#fb923c'};
    font-size: ${props => props.theme.typography?.fontSize?.lg || '1.125rem'};
    font-weight: ${props => props.theme.typography?.fontWeight?.semibold || '600'};
    display: block;
    margin-bottom: ${props => props.theme.spacing?.[2] || '0.5rem'};
  }
`;

const ProfileLocation = styled(Text)`
  && {
    color: ${props => props.theme.colors?.neutral?.[300] || '#d4d4d4'};
    display: flex;
    align-items: center;
    gap: ${props => props.theme.spacing?.[1] || '0.25rem'};
    margin-bottom: ${props => props.theme.spacing?.[4] || '1rem'};
  }
`;

const SectionCard = styled(Card)`
  && {
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: ${props => props.theme.borderRadius?.lg || '0.5rem'};
    margin-bottom: ${props => props.theme.spacing?.[4] || '1rem'};
    
    .ant-card-head {
      background: transparent;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      
      .ant-card-head-title {
        color: ${props => props.theme.colors?.neutral?.[50] || '#fafafa'};
        font-weight: ${props => props.theme.typography?.fontWeight?.bold || '700'};
      }
    }
    
    .ant-card-body {
      background: transparent;
    }
  }
`;

const SkillTag = styled.span`
  background: ${props => props.theme.colors?.secondary?.[500] || '#f97316'};
  color: white;
  padding: ${props => props.theme.spacing?.[1] || '0.25rem'} ${props => props.theme.spacing?.[3] || '0.75rem'};
  border-radius: ${props => props.theme.borderRadius?.full || '9999px'};
  font-size: ${props => props.theme.typography?.fontSize?.sm || '0.875rem'};
  font-weight: ${props => props.theme.typography?.fontWeight?.medium || '500'};
  margin-right: ${props => props.theme.spacing?.[2] || '0.5rem'};
  margin-bottom: ${props => props.theme.spacing?.[2] || '0.5rem'};
  display: inline-block;
`;

const InterestTag = styled.span`
  background: rgba(226, 39, 132, 0.8);
  color: white;
  padding: ${props => props.theme.spacing?.[1] || '0.25rem'} ${props => props.theme.spacing?.[3] || '0.75rem'};
  border-radius: ${props => props.theme.borderRadius?.full || '9999px'};
  font-size: ${props => props.theme.typography?.fontSize?.sm || '0.875rem'};
  font-weight: ${props => props.theme.typography?.fontWeight?.medium || '500'};
  margin-right: ${props => props.theme.spacing?.[2] || '0.5rem'};
  margin-bottom: ${props => props.theme.spacing?.[2] || '0.5rem'};
  display: inline-block;
`;

const ExperienceItem = styled.div`
  margin-bottom: ${props => props.theme.spacing?.[4] || '1rem'};
  padding-bottom: ${props => props.theme.spacing?.[4] || '1rem'};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const ExperienceTitle = styled(Text)`
  && {
    color: ${props => props.theme.colors?.neutral?.[50] || '#fafafa'};
    font-weight: ${props => props.theme.typography?.fontWeight?.semibold || '600'};
    font-size: ${props => props.theme.typography?.fontSize?.base || '1rem'};
    display: block;
  }
`;

const ExperienceCompany = styled(Text)`
  && {
    color: ${props => props.theme.colors?.secondary?.[400] || '#fb923c'};
    font-weight: ${props => props.theme.typography?.fontWeight?.medium || '500'};
    display: block;
  }
`;

const ExperienceDuration = styled(Text)`
  && {
    color: ${props => props.theme.colors?.neutral?.[400] || '#a3a3a3'};
    font-size: ${props => props.theme.typography?.fontSize?.sm || '0.875rem'};
    display: block;
    margin-bottom: ${props => props.theme.spacing?.[2] || '0.5rem'};
  }
`;

const ExperienceDescription = styled(Paragraph)`
  && {
    color: ${props => props.theme.colors?.neutral?.[300] || '#d4d4d4'};
    margin-bottom: 0;
    font-size: ${props => props.theme.typography?.fontSize?.sm || '0.875rem'};
  }
`;

const ProfileDemo = () => {
  return (
    <ProfileContainer>
      <ProfileHeader />
      
      <ProfileContent>
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={8}>
            <ProfileInfo>
              <ProfileAvatar 
                src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face"
                alt="Profile"
              />
              <ProfileName level={3}>
                {mockProfileData.firstName} {mockProfileData.lastName}
              </ProfileName>
              <ProfileTitle>{mockProfileData.title}</ProfileTitle>
              <ProfileLocation>
                <EnvironmentOutlined />
                {mockProfileData.location}
              </ProfileLocation>
              <ProfileLocation>
                <MailOutlined />
                {mockProfileData.email}
              </ProfileLocation>
            </ProfileInfo>
          </Col>
          
          <Col xs={24} lg={16}>
            <SectionCard title="About" size="small">
              <Paragraph style={{ color: '#d4d4d4', marginBottom: 0 }}>
                {mockProfileData.bio}
              </Paragraph>
            </SectionCard>
            
            <SectionCard title="Skills" size="small">
              <div>
                {mockProfileData.skills.map((skill, index) => (
                  <SkillTag key={index}>{skill}</SkillTag>
                ))}
              </div>
            </SectionCard>
            
            <SectionCard title="Interests" size="small">
              <div>
                {mockProfileData.interests.map((interest, index) => (
                  <InterestTag key={index}>{interest}</InterestTag>
                ))}
              </div>
            </SectionCard>
            
            <SectionCard title="Experience" size="small">
              {mockProfileData.experience.map((exp, index) => (
                <ExperienceItem key={index}>
                  <ExperienceTitle>{exp.title}</ExperienceTitle>
                  <ExperienceCompany>{exp.company}</ExperienceCompany>
                  <ExperienceDuration>{exp.duration}</ExperienceDuration>
                  <ExperienceDescription>{exp.description}</ExperienceDescription>
                </ExperienceItem>
              ))}
            </SectionCard>
            
            <SectionCard title="Education" size="small">
              <ExperienceTitle>{mockProfileData.education.degree}</ExperienceTitle>
              <ExperienceCompany>{mockProfileData.education.school}</ExperienceCompany>
              <ExperienceDuration>{mockProfileData.education.duration}</ExperienceDuration>
            </SectionCard>
          </Col>
        </Row>
      </ProfileContent>
    </ProfileContainer>
  );
};

export default ProfileDemo;
